// Maps Slot Availability Calendar API responses to the UI types in
// interfaces/SlotCalendarObject.ts.
//
// Every field in that interfaces file already matches the backend's own
// camelCase names (see that file's header comment), so this convertor's job
// is defensive coercion — never let a missing/misshapen field crash the
// widget — rather than renaming. Kept as the single place that would need to
// change if the backend ever does rename something, per this repo's existing
// "convertors are the seam" convention (see ApiEnvelopeHelper's own callers).

import {
  CreateSlotScheduleVersionInput,
  SlotAvailabilityPage,
  SlotAvailabilitySlot,
  SlotAvailabilityStatus,
  SlotBlock,
  SlotBlockOperationalStatus,
  SlotBlockPreview,
  SlotBookingEntry,
  SlotBookingEntryStatus,
  SlotCalendarAudit,
  SlotCalendarChange,
  SlotCalendarMeta,
  SlotCalendarMutationResult,
  SlotCalendarPage,
  SlotCalendarPreview,
  SlotCalendarPreviewConflict,
  SlotCalendarPreviewSlot,
  SlotCalendarResourceType,
  SlotCalendarState,
  SlotCalendarSummary,
  SlotCalendarWarning,
  SlotEntityChange,
  SlotKind,
  SlotOccurrenceLocator,
  SlotOperationalStatus,
  SlotOwnerAvailabilityPage,
  SlotOwnerAvailabilitySlot,
  SlotScheduleRule,
  SlotScheduleVersion,
  SlotSummaryBucketItem,
} from "../../../interfaces/SlotCalendarObject";

// ─── Small scalar guards ─────────────────────────────────────────────────────

function str(value: unknown, fallback = ""): string {
  return typeof value === "string" ? value : fallback;
}

function strOrNull(value: unknown): string | null {
  return typeof value === "string" ? value : null;
}

function num(value: unknown, fallback = 0): number {
  return typeof value === "number" && Number.isFinite(value) ? value : fallback;
}

function numOrNull(value: unknown): number | null {
  return typeof value === "number" && Number.isFinite(value) ? value : null;
}

function bool(value: unknown, fallback = false): boolean {
  return typeof value === "boolean" ? value : fallback;
}

function record(value: unknown): Record<string, unknown> {
  return value && typeof value === "object" && !Array.isArray(value) ? (value as Record<string, unknown>) : {};
}

function arr(value: unknown): unknown[] {
  return Array.isArray(value) ? value : [];
}

// ─── Shared value objects ────────────────────────────────────────────────────

export function convertSlotCalendarAudit(raw: unknown): SlotCalendarAudit | null {
  if (!raw || typeof raw !== "object") return null;
  const rec = raw as Record<string, unknown>;
  return {
    createdAt: strOrNull(rec.createdAt),
    createdBy: strOrNull(rec.createdBy),
    updatedAt: strOrNull(rec.updatedAt),
    updatedBy: strOrNull(rec.updatedBy),
  };
}

export function convertSlotOccurrenceLocator(raw: unknown): SlotOccurrenceLocator {
  const rec = record(raw);
  return {
    startsAt: str(rec.startsAt),
    scheduleVersionId: strOrNull(rec.scheduleVersionId),
  };
}

export function convertSlotScheduleRule(raw: unknown): SlotScheduleRule {
  const rec = record(raw);
  return {
    ruleId: str(rec.ruleId),
    anchorDate: str(rec.anchorDate),
    untilDate: str(rec.untilDate),
    repeatEvery: num(rec.repeatEvery, 1),
    repeatUnit: (str(rec.repeatUnit, "WEEK") as SlotScheduleRule["repeatUnit"]),
    weekdays: Array.isArray(rec.weekdays) ? rec.weekdays.map((w) => num(w)) : null,
    localStartTime: strOrNull(rec.localStartTime),
    localEndTime: strOrNull(rec.localEndTime),
  };
}

export function convertSlotCalendarWarning(raw: unknown): SlotCalendarWarning {
  // Tolerate a bare string until a live response confirms the structured
  // {code?, message} shape the backend doc describes only loosely.
  if (typeof raw === "string") return { message: raw };
  const rec = record(raw);
  return {
    code: typeof rec.code === "string" ? rec.code : undefined,
    message: str(rec.message, "Something to review."),
  };
}

// ─── Schedule versions ───────────────────────────────────────────────────────

export function convertSlotScheduleVersion(raw: unknown): SlotScheduleVersion {
  const rec = record(raw);
  return {
    scheduleVersionId: str(rec.scheduleVersionId),
    calendarId: str(rec.calendarId),
    revision: num(rec.revision),
    effectiveFrom: str(rec.effectiveFrom),
    availabilityUntilExclusive: str(rec.availabilityUntilExclusive),
    kind: (str(rec.kind, "CUSTOM") as SlotKind),
    durationValue: num(rec.durationValue, 1),
    durationUnit: (str(rec.durationUnit, "HOUR") as SlotScheduleVersion["durationUnit"]),
    defaultCapacity: num(rec.defaultCapacity, 1),
    capacityLabelSingular: str(rec.capacityLabelSingular, "slot"),
    capacityLabelPlural: str(rec.capacityLabelPlural, "slots"),
    bookingCutoff: (str(rec.bookingCutoff, "START") as SlotScheduleVersion["bookingCutoff"]),
    defaultPublicLabel: strOrNull(rec.defaultPublicLabel),
    defaultProperties: record(rec.defaultProperties),
    rules: arr(rec.rules).map(convertSlotScheduleRule),
    schemaVersion: num(rec.schemaVersion, 1),
    engineVersion: strOrNull(rec.engineVersion),
    tzdbVersion: strOrNull(rec.tzdbVersion),
    createdAt: strOrNull(rec.createdAt),
    createdBy: strOrNull(rec.createdBy),
  };
}

function convertScheduleVersionOrNull(raw: unknown): SlotScheduleVersion | null {
  return raw && typeof raw === "object" ? convertSlotScheduleVersion(raw) : null;
}

// ─── Calendar ────────────────────────────────────────────────────────────────

export function convertSlotCalendarMeta(raw: unknown): SlotCalendarMeta {
  const rec = record(raw);
  return {
    calendarId: str(rec.calendarId),
    productId: str(rec.productId),
    timezone: str(rec.timezone, "Asia/Kolkata"),
    published: bool(rec.published),
    calendarRevision: num(rec.calendarRevision),
    state: (str(rec.state, "UNCONFIGURED") as SlotCalendarState),
    currentScheduleVersion: convertScheduleVersionOrNull(rec.currentScheduleVersion),
    nextScheduleVersion: convertScheduleVersionOrNull(rec.nextScheduleVersion),
    audit: convertSlotCalendarAudit(rec.audit),
  };
}

// ─── Availability (read) ─────────────────────────────────────────────────────

export function convertSlotAvailabilitySlot(raw: unknown): SlotAvailabilitySlot {
  const rec = record(raw);
  const capacity = num(rec.capacity);
  const occupiedCapacity = num(rec.occupiedCapacity);
  const occurrence = convertSlotOccurrenceLocator(rec.occurrence);
  return {
    slotId: str(rec.slotId),
    occurrence,
    // The occurrence locator always carries the start - fall back to it.
    startsAt: str(rec.startsAt) || occurrence.startsAt,
    endsAt: str(rec.endsAt),
    kind: (str(rec.kind, "CUSTOM") as SlotKind),
    capacity,
    occupiedCapacity,
    // remainingCapacity is server-computed and should always be present, but
    // a defensive fallback keeps a malformed row from reading as MORE
    // available than it is (never invent capacity the server didn't confirm).
    remainingCapacity: numOrNull(rec.remainingCapacity) ?? Math.max(capacity - occupiedCapacity, 0),
    capacityLabelSingular: str(rec.capacityLabelSingular, "slot"),
    capacityLabelPlural: str(rec.capacityLabelPlural, "slots"),
    operationalStatus: (str(rec.operationalStatus, "OPEN") as SlotOperationalStatus),
    availabilityStatus: (str(rec.availabilityStatus, "UNAVAILABLE") as SlotAvailabilityStatus),
    past: bool(rec.past),
    publicLabel: strOrNull(rec.publicLabel),
    publicProperties: record(rec.publicProperties),
  };
}

export function convertSlotOwnerAvailabilitySlot(raw: unknown): SlotOwnerAvailabilitySlot {
  const rec = record(raw);
  // Live shape (OwnerAvailabilitySlotRs): the public slot fields are nested
  // under `slot`, owner-only fields sit beside it. Flat is still accepted.
  const base = convertSlotAvailabilitySlot(rec.slot ?? raw);
  return {
    ...base,
    materialized: bool(rec.materialized),
    sourceScheduleVersionId: strOrNull(rec.sourceScheduleVersionId),
    sourceRuleId: strOrNull(rec.sourceRuleId),
    statusOverride: rec.statusOverride ? (str(rec.statusOverride) as SlotOperationalStatus) : null,
    effectiveStatusRevision: numOrNull(rec.effectiveStatusRevision),
    bookingCutoffAt: str(rec.bookingCutoffAt),
    privateNote: strOrNull(rec.privateNote),
    archivedAt: strOrNull(rec.archivedAt),
    activeEntryCount: num(rec.activeEntryCount),
    // Live field is `activeEntryPreview`; `entryPreview` kept as a fallback.
    entryPreview: arr(rec.activeEntryPreview ?? rec.entryPreview).map((entry) => {
      const e = record(entry);
      return { name: str(e.nameOrReference ?? e.name, "Guest"), quantity: num(e.quantity, 1) };
    }),
    audit: convertSlotCalendarAudit(rec.audit),
  };
}

export function convertSlotAvailabilityPage(raw: unknown): SlotAvailabilityPage {
  const rec = record(raw);
  return {
    calendarId: str(rec.calendarId),
    productId: str(rec.productId),
    timezone: str(rec.timezone, "Asia/Kolkata"),
    calendarRevision: num(rec.calendarRevision),
    asOf: str(rec.asOf),
    from: str(rec.from),
    to: str(rec.to),
    items: arr(rec.items).map(convertSlotAvailabilitySlot),
    nextCursor: strOrNull(rec.nextCursor),
    hasMore: bool(rec.hasMore),
  };
}

export function convertSlotOwnerAvailabilityPage(raw: unknown): SlotOwnerAvailabilityPage {
  const rec = record(raw);
  return {
    calendarId: str(rec.calendarId),
    productId: str(rec.productId),
    timezone: str(rec.timezone, "Asia/Kolkata"),
    calendarRevision: num(rec.calendarRevision),
    asOf: str(rec.asOf),
    from: str(rec.from),
    to: str(rec.to),
    items: arr(rec.items).map(convertSlotOwnerAvailabilitySlot),
    nextCursor: strOrNull(rec.nextCursor),
    hasMore: bool(rec.hasMore),
  };
}

export function convertSlotSummaryBucketItem(raw: unknown): SlotSummaryBucketItem {
  const rec = record(raw);
  return {
    fromDate: str(rec.fromDate),
    toDateExclusive: str(rec.toDateExclusive),
    occurrenceCount: num(rec.occurrenceCount),
    availableCount: num(rec.availableCount),
    fullCount: num(rec.fullCount),
    maintenanceCount: num(rec.maintenanceCount),
    unavailableCount: num(rec.unavailableCount),
    pastCount: num(rec.pastCount),
  };
}

export function convertSlotCalendarSummary(raw: unknown): SlotCalendarSummary {
  const rec = record(raw);
  return {
    calendarId: str(rec.calendarId),
    productId: str(rec.productId),
    timezone: str(rec.timezone, "Asia/Kolkata"),
    calendarRevision: num(rec.calendarRevision),
    asOf: str(rec.asOf),
    bucket: (str(rec.bucket, "DAY") as SlotCalendarSummary["bucket"]),
    fromDate: str(rec.fromDate),
    toDateExclusive: str(rec.toDateExclusive),
    buckets: arr(rec.buckets).map(convertSlotSummaryBucketItem),
  };
}

// ─── Booking entries ─────────────────────────────────────────────────────────

export function convertSlotBookingEntry(raw: unknown): SlotBookingEntry {
  const rec = record(raw);
  return {
    entryId: str(rec.entryId),
    slotId: str(rec.slotId),
    calendarId: str(rec.calendarId),
    quantity: num(rec.quantity, 1),
    nameOrReference: str(rec.nameOrReference),
    privateNote: strOrNull(rec.privateNote),
    status: (str(rec.status, "ACTIVE") as SlotBookingEntryStatus),
    cancelledAt: strOrNull(rec.cancelledAt),
    calendarRevision: num(rec.calendarRevision),
    audit: convertSlotCalendarAudit(rec.audit),
  };
}

// ─── Blocks ──────────────────────────────────────────────────────────────────

export function convertSlotBlock(raw: unknown): SlotBlock {
  const rec = record(raw);
  return {
    blockId: str(rec.blockId),
    calendarId: str(rec.calendarId),
    calendarRevision: num(rec.calendarRevision),
    eventRevision: num(rec.eventRevision),
    startsAt: str(rec.startsAt),
    endsAt: str(rec.endsAt),
    operationalStatus: (str(rec.operationalStatus, "OPEN") as SlotBlockOperationalStatus),
    privateReason: strOrNull(rec.privateReason),
    appliesAfter: str(rec.appliesAfter),
    audit: convertSlotCalendarAudit(rec.audit),
  };
}

// ─── Mutation result ─────────────────────────────────────────────────────────

export function convertSlotCalendarMutationResult(raw: unknown): SlotCalendarMutationResult {
  const rec = record(raw);
  return {
    operationId: str(rec.operationId),
    calendarId: str(rec.calendarId),
    calendarRevision: num(rec.calendarRevision),
    resourceType: (str(rec.resourceType, "CALENDAR") as SlotCalendarResourceType),
    resourceId: str(rec.resourceId),
    slotId: strOrNull(rec.slotId),
    affectedBookedSlotCount: num(rec.affectedBookedSlotCount),
    warnings: arr(rec.warnings).map(convertSlotCalendarWarning),
  };
}

// ─── Previews ────────────────────────────────────────────────────────────────

function convertPreviewSlot(raw: unknown): SlotCalendarPreviewSlot {
  const rec = record(raw);
  return {
    startsAt: str(rec.startsAt),
    endsAt: str(rec.endsAt),
    kind: (str(rec.kind, "CUSTOM") as SlotKind),
    capacity: num(rec.capacity),
  };
}

function convertPreviewConflict(raw: unknown): SlotCalendarPreviewConflict {
  const rec = record(raw);
  return {
    startsAt: str(rec.startsAt),
    endsAt: strOrNull(rec.endsAt),
    reason: str(rec.reason, "Conflicts with existing availability."),
  };
}

export function convertSlotCalendarPreview(raw: unknown): SlotCalendarPreview {
  const rec = record(raw);
  return {
    timezone: str(rec.timezone, "Asia/Kolkata"),
    calendarRevision: numOrNull(rec.calendarRevision),
    effectiveFrom: str(rec.effectiveFrom),
    effectiveToExclusive: strOrNull(rec.effectiveToExclusive),
    fromDate: str(rec.fromDate),
    toDateExclusive: str(rec.toDateExclusive),
    totalOccurrenceCount: num(rec.totalOccurrenceCount),
    newVirtualOccurrenceCount: num(rec.newVirtualOccurrenceCount),
    preservedInstanceCount: num(rec.preservedInstanceCount),
    removedUntouchedOccurrenceCount: num(rec.removedUntouchedOccurrenceCount),
    conflictCount: num(rec.conflictCount),
    conflicts: arr(rec.conflicts).map(convertPreviewConflict),
    sampleSlots: arr(rec.sampleSlots).map(convertPreviewSlot),
    sampleTruncated: bool(rec.sampleTruncated),
    warnings: arr(rec.warnings).map(convertSlotCalendarWarning),
  };
}

export function convertSlotBlockPreview(raw: unknown): SlotBlockPreview {
  const rec = record(raw);
  return {
    calendarRevision: num(rec.calendarRevision),
    affectedOccurrenceCount: num(rec.affectedOccurrenceCount),
    affectedBookedSlotCount: num(rec.affectedBookedSlotCount),
    affectedActiveQuantity: num(rec.affectedActiveQuantity),
    skippedPastOccurrenceCount: num(rec.skippedPastOccurrenceCount),
    sampleSlots: arr(rec.sampleSlots).map(convertPreviewSlot),
    sampleTruncated: bool(rec.sampleTruncated),
    warnings: arr(rec.warnings).map(convertSlotCalendarWarning),
  };
}

// ─── Change history ──────────────────────────────────────────────────────────

function convertSlotEntityChange(raw: unknown): SlotEntityChange {
  const rec = record(raw);
  return {
    entityType: str(rec.entityType),
    entityId: str(rec.entityId),
    before: rec.before && typeof rec.before === "object" ? record(rec.before) : null,
    after: rec.after && typeof rec.after === "object" ? record(rec.after) : null,
  };
}

export function convertSlotCalendarChange(raw: unknown): SlotCalendarChange {
  const rec = record(raw);
  return {
    calendarId: str(rec.calendarId),
    revision: num(rec.revision),
    operationId: str(rec.operationId),
    actorId: str(rec.actorId),
    operationType: str(rec.operationType),
    recordedAt: str(rec.recordedAt),
    changes: arr(rec.changes).map(convertSlotEntityChange),
  };
}

// ─── Generic paged wrapper ───────────────────────────────────────────────────

/** See SlotCalendarPage<T>'s own doc comment for why the exact field names
 *  here are a best guess pending a live response. */
export function convertSlotCalendarPage<T>(raw: unknown, convertItem: (item: unknown) => T): SlotCalendarPage<T> {
  const rec = record(raw);
  return {
    calendarId: str(rec.calendarId),
    calendarRevision: num(rec.calendarRevision),
    asOf: str(rec.asOf),
    items: arr(rec.items).map(convertItem),
    nextCursor: strOrNull(rec.nextCursor),
    hasMore: bool(rec.hasMore),
  };
}

/** Reconstructs a CreateSlotScheduleVersionInput from a read SlotScheduleVersion
 *  — used by the (future) Add-slots flow to seed "current rules + one new
 *  rule" without hand-copying every field. Kept here since it's a pure
 *  reshape of already-converted UI data, not a raw-response parse. */
export function scheduleVersionToCreateInput(version: SlotScheduleVersion): CreateSlotScheduleVersionInput {
  return {
    effectiveFrom: version.effectiveFrom,
    availabilityUntilExclusive: version.availabilityUntilExclusive,
    kind: version.kind,
    durationValue: version.durationValue,
    durationUnit: version.durationUnit,
    defaultCapacity: version.defaultCapacity,
    capacityLabelSingular: version.capacityLabelSingular,
    capacityLabelPlural: version.capacityLabelPlural,
    bookingCutoff: version.bookingCutoff,
    defaultPublicLabel: version.defaultPublicLabel,
    defaultProperties: version.defaultProperties,
    rules: version.rules,
  };
}
